<template>
  <div
    class="h-full w-screen flex-1 justify-center px-6 pb-6 pt-12 transition-opacity"
    :class="[
      visible
        ? 'order-2 animate-appear'
        : 'pointer-events-none order-3 hidden opacity-0',
    ]"
    data-testid="questionaire-result"
  >
    <h2 class="mb-6 text-2xl font-medium sm:text-center sm:text-4xl">
      {{ $t('contact_lens_consultant.result_screen.headline') }}
    </h2>
    <div
      class="mb-6 flex snap-x snap-mandatory justify-start gap-3 overflow-x-auto pb-4 sm:justify-center"
    >
      <button
        v-for="(item, index) in items"
        :key="'filter-' + index"
        type="button"
        class="whitespace-nowrap rounded-xl bg-shade-200 px-4 py-2 font-medium text-black transition-colors hover:bg-shade-300"
        :class="{
          'bg-shade-300':
            item.product.referenceKey === selectedProduct?.referenceKey,
        }"
        @click="selectProduct(item)"
      >
        {{ item.badge }}
      </button>
    </div>
    <div class="mx-auto max-w-full" :style="sliderStyles">
      <HorizontalItemsScroller
        ref="slider"
        :loop="false"
        :autoplay="false"
        :has-gradient="false"
        :wrapper-classes="{ 'gap-6': true }"
      >
        <ProductSelectOption
          v-for="(item, index) in items"
          :key="'slide-' + index"
          class="item relative flex w-full shrink-0 grow-0 snap-start items-stretch justify-center xs:w-[21rem]"
          :product="item.product"
          :selected="
            item.product.referenceKey === selectedProduct?.referenceKey
          "
          @click:select="selectProduct(item)"
        >
          <template #badge>
            <div
              class="inline-flex items-center rounded-lg px-2 py-1 font-medium"
              :class="{
                'bg-[#FFA943] text-shade-700': isBadgeHighlight(item),
                'bg-shade-700 text-white': !isBadgeHighlight(item),
              }"
            >
              <slot>{{ item.badge }}</slot>
            </div>
          </template>
        </ProductSelectOption>
      </HorizontalItemsScroller>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-core'
import {
  type QuestionaireResultItem,
  QuestionaireBadgeItem,
} from '~/constants/questionaire'

import type HorizontalItemsScroller from '~/components/ui/sliders/HorizontalItemsScroller.vue'

const { visible = false, items } = defineProps<{
  visible?: boolean
  items?: QuestionaireResultItem[]
}>()

const isBadgeHighlight = (item: QuestionaireResultItem) =>
  item.type &&
  ![QuestionaireBadgeItem.Premium, QuestionaireBadgeItem.Second].includes(
    item.type,
  )

const emit = defineEmits<{
  (e: 'click:close' | 'click:open' | 'restart'): void
  (e: 'show:result', params: QuestionaireResultItem[]): void
  (e: 'select:product', params: QuestionaireResultItem): void
}>()

const slider = ref<InstanceType<typeof HorizontalItemsScroller>>()

// Product
const selectedProduct = ref<Product>()

const activeProductIndex = computed(() => {
  return (
    items?.findIndex(
      (item) =>
        item.product.referenceKey === selectedProduct.value?.referenceKey,
    ) ?? 0
  )
})

watch(activeProductIndex, (next) => {
  if (slider.value && typeof next !== 'undefined') {
    slider.value.slideTo(next)
  }
})

const selectProduct = (item: QuestionaireResultItem) => {
  emit('select:product', item)
  selectedProduct.value = item.product
}

const reset = () => {
  selectedProduct.value = undefined
}

const sliderStyles = computed(() => {
  const count = items?.length ?? 1
  return { width: `${21 * count + 1.5 * (count - 1)}rem` }
})

defineExpose({ reset })
</script>
